import React from 'react';
import { CircleCheck, CircleX, Loader2, Minus, TriangleAlert, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { BatchStatus } from '../../types/batch.types';
import { formatAxisValueForDisplay } from '../../types';

interface BatchProgressProps {
  batchStatus: BatchStatus;
  isComplete: boolean;
  onBackToCatalog: () => void;
  onClose?: () => void;
}

/**
 * The one glyph that says where the batch is: done, done-with-failures, or
 * still running. Rendered at two sizes — the minimized pill and the header.
 */
const BatchStateIcon: React.FC<{ isComplete: boolean; hasErrors: boolean; className?: string }> = ({
  isComplete,
  hasErrors,
  className,
}) => {
  if (!isComplete) {
    return <Loader2 className={cn('animate-spin text-muted-foreground', className)} />;
  }
  return hasErrors ? (
    <TriangleAlert className={cn('text-destructive', className)} />
  ) : (
    <CircleCheck className={cn('text-success', className)} />
  );
};

/**
 * Batch processing progress indicator
 * Shows as a fixed card in the bottom-right corner during and after product submission
 */
export const BatchProgress: React.FC<BatchProgressProps> = ({
  batchStatus,
  isComplete,
  onBackToCatalog,
  onClose,
}) => {
  const { t } = useTranslation();
  const [isMinimized, setIsMinimized] = React.useState(false);

  const processedCount = batchStatus.completed + batchStatus.failed;
  const hasErrors = batchStatus.failed > 0;

  // Minimized state — compact pill
  if (isMinimized) {
    return (
      <div
        onClick={() => setIsMinimized(false)}
        className="fixed bottom-[calc(1.5rem+var(--support-fab-clearance,0px))] end-6 z-[1300] flex cursor-pointer items-center gap-2.5 border bg-background/95 px-4 py-2.5 backdrop-blur-md transition-colors hover:bg-muted"
      >
        <BatchStateIcon isComplete={isComplete} hasErrors={hasErrors} className="size-4" />
        <span className="text-sm font-medium text-foreground">
          {isComplete
            ? t('PAGES.NEW_PRODUCT.V3.BATCH.PROCESSING_COMPLETE')
            : t('PAGES.NEW_PRODUCT.V3.BATCH.PROCESSING', { percentage: batchStatus.percentage })
          }
        </span>
      </div>
    );
  }

  // Full panel
  return (
    <div className="fixed bottom-[calc(1.5rem+var(--support-fab-clearance,0px))] end-6 z-[1300] flex max-h-[70vh] w-[380px] flex-col overflow-hidden border bg-background/95 backdrop-blur-md">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-5 py-3.5">
        <div className="flex items-center gap-2.5">
          <BatchStateIcon isComplete={isComplete} hasErrors={hasErrors} className="size-[18px]" />
          <span className="text-sm font-medium text-foreground">
            {isComplete
              ? (hasErrors
                  ? t('PAGES.NEW_PRODUCT.V3.BATCH.PROCESSING_COMPLETE')
                  : t('PAGES.NEW_PRODUCT.V3.BATCH.PROCESSING_COMPLETE'))
              : t('PAGES.NEW_PRODUCT.V3.BATCH.PROCESSING_VARIANTS')
            }
          </span>
        </div>
        <div className="flex items-center gap-0.5">
          {/* Minimize */}
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={() => setIsMinimized(true)}
            aria-label={t('COMMON.MINIMIZE', 'Minimize') as string}
          >
            <Minus />
          </Button>
          {/* Close */}
          {isComplete && onClose && (
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={onClose}
              aria-label={t('COMMON.CLOSE') as string}
            >
              <X />
            </Button>
          )}
        </div>
      </div>

      {/* Progress bar + stats */}
      <div className="px-5 pt-4 pb-3">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs font-medium text-muted-foreground">
            {t('PAGES.NEW_PRODUCT.V3.BATCH.VARIANTS_COUNT', { completed: processedCount, total: batchStatus.total })}
          </span>
          <span className="text-xs font-medium text-foreground">{batchStatus.percentage}%</span>
        </div>
        <Progress
          value={batchStatus.percentage}
          className={cn(
            isComplete && hasErrors && '[&_[data-slot=progress-indicator]]:bg-destructive',
            isComplete && !hasErrors && '[&_[data-slot=progress-indicator]]:bg-success'
          )}
        />

        {/* Error/success summary chips */}
        {isComplete && (
          <div className="mt-3 flex gap-2">
            {batchStatus.completed > 0 && (
              <span className="inline-flex items-center gap-1 bg-success/10 px-2.5 py-0.5 text-[11px] font-medium text-success">
                <span className="size-1 rounded-full bg-success" />
                {batchStatus.completed} {t('PAGES.NEW_PRODUCT.V3.BATCH.SUCCEEDED')}
              </span>
            )}
            {hasErrors && (
              <span className="inline-flex items-center gap-1 bg-destructive/10 px-2.5 py-0.5 text-[11px] font-medium text-destructive">
                <span className="size-1 rounded-full bg-destructive" />
                {batchStatus.failed} {t('PAGES.NEW_PRODUCT.V3.BATCH.FAILED')}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Variants List */}
      <div className="flex max-h-[40vh] flex-col gap-1 overflow-y-auto px-5 pb-2">
        {batchStatus.jobs && batchStatus.jobs.length > 0 ? (
          batchStatus.jobs.map((job, index) => {
            const pd = job.productData;
            const productName = pd?.name || 'Unknown Product';
            const thumbnailPath = job.resultProduct?.thumbnailPath;
            const operation = job.operation || 'update';
            const status = job.status;

            // Build variant display text from axes
            const axes = pd?.axes;
            const variantDetails = axes
              ? Object.values(axes)
                  .map(v => formatAxisValueForDisplay(v as any))
                  .filter(Boolean)
                  .join(' · ')
              : '';

            // Status indicator
            let statusIcon: React.ReactNode;
            if (status === 'completed') {
              statusIcon = <CircleCheck className="size-3.5 text-success" />;
            } else if (status === 'failed') {
              statusIcon = <CircleX className="size-3.5 text-destructive" />;
            } else if (status === 'processing' || status === 'generating_seamless') {
              statusIcon = <Loader2 className="size-3.5 animate-spin text-muted-foreground" />;
            } else {
              statusIcon = <span className="size-2.5 rounded-full border-[1.5px] border-border" />;
            }

            // Operation label
            let operationLabel: { text: string; className: string } | null = null;
            if (operation === 'create') {
              operationLabel = { text: t('PAGES.NEW_PRODUCT.V3.BATCH.CREATE'), className: 'text-success' };
            } else if (operation === 'update') {
              operationLabel = { text: t('PAGES.NEW_PRODUCT.V3.BATCH.UPDATE'), className: 'text-muted-foreground' };
            } else if (operation === 'delete') {
              operationLabel = { text: t('PAGES.NEW_PRODUCT.V3.BATCH.DELETE'), className: 'text-destructive' };
            }

            return (
              <div
                key={job.jobId || index}
                className="flex items-center gap-2.5 px-2.5 py-2 transition-colors hover:bg-muted"
              >
                {/* Status Icon */}
                <div className="flex shrink-0 items-center">{statusIcon}</div>

                {/* Thumbnail */}
                {thumbnailPath && (
                  <div className="size-8 shrink-0 overflow-hidden border bg-muted">
                    <img src={thumbnailPath} alt={productName} className="size-full object-cover" />
                  </div>
                )}

                {/* Variant Info */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate text-xs font-medium text-foreground">
                      {variantDetails || productName}
                    </span>
                    {operationLabel && (
                      <span className={cn('shrink-0 text-[10px] font-medium', operationLabel.className)}>
                        {operationLabel.text}
                      </span>
                    )}
                  </div>
                  {status === 'failed' && job.errorDetails && (
                    <p className="mt-0.5 truncate text-[11px] text-destructive">
                      {job.errorDetails.userMessage || job.error}
                    </p>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <p className="py-4 text-center text-xs text-muted-foreground">
            {t('PAGES.NEW_PRODUCT.V3.BATCH.NO_VARIANTS')}
          </p>
        )}
      </div>

      {/* Action Buttons */}
      {isComplete && (
        <div className="flex gap-2 border-t px-5 py-4">
          {onClose && (
            <Button variant="outline" className="h-9 flex-1" onClick={onClose}>
              {t('PAGES.NEW_PRODUCT.V3.BATCH.CONTINUE_EDITING')}
            </Button>
          )}
          <Button variant="outline" className="h-9 flex-1" onClick={onBackToCatalog}>
            {t('PAGES.NEW_PRODUCT.BACK_TO_CATALOG')}
          </Button>
        </div>
      )}
    </div>
  );
};
